import { createAsyncThunk } from '@reduxjs/toolkit';

import { actions } from './';
import { selectAuthAlerts, selectIsQrCodeRecognized } from './selectors';
import authenticationResultsApi from '../../apis/authenticationResults';
import history from '../../history';
import productApi from '../../apis/product';
import streamApi from '../../apis/stream';
import thngApi from '../../apis/thng';
import { generateAlertTrackingEvents } from '../../components/GoogleTagManager';

export const scanQr = createAsyncThunk(
	'scanSession/scanQr',
	async (thngId, { dispatch, getState }) => {
		if (thngId) {
			dispatch(validateQr(thngId));
			dispatch(findProduct(thngId)); // implicit scan means we don't get product id for free, must make a separate call
		} else {
			const { setToastOpen, setHasCameraPermission } = actions;

			try {
				const res = await streamApi.scan({
					filter: { method: '2d', type: 'qr_code' },
					containerId: 'stream_container',
				}); // start the qr code scan

				if (res.length === 0) {
					// no results found
					dispatch(scanQr()); // run this function again
				} else if (res && res[0].results.length === 0) {
					// irrelevant result found
					dispatch(setToastOpen(true));
					dispatch(scanQr());
				} else {
					// valid result found
					dispatch(validateQr(res[0].results[0].thng.id));
					dispatch(getProduct(res[0].results[0].thng.product));
				}
			} catch (e) {
				console.error(e);

				if (e.message === 'Permission denied') {
					dispatch(setHasCameraPermission(false));
				}
			}
		}
	}
);

export const validateQr = createAsyncThunk(
	'scanSession/validateQr',
	async (thngId, { dispatch, getState }) => {
		const { setQrResults } = actions;
		let scanId;

		const socket = await authenticationResultsApi.subscribe(
			thngId,
			async (unserializableMessage) => {
				const message = JSON.parse(JSON.stringify(unserializableMessage)); // evt's package returns an instantiated class which cannot be stored in redux.

				if (message.customFields.triggerAction === scanId) {
					socket.unsubscribe();
					await dispatch(setQrResults(message));

					const isAuthenticated = selectIsQrCodeRecognized(await getState());
					streamApi.stop();

					const alerts = selectAuthAlerts(getState());
					if (!isAuthenticated) {
						generateAlertTrackingEvents('step1', alerts);
					}

					await history.push(
						isAuthenticated
							? '/step1/success'
							: '/verificationResults/counterfeit'
					);
				}
			}
		);

		try {
			scanId = (await thngApi.scan(thngId)).id; // trigger the scan
		} catch (e) {
			console.error(e);
		}
	}
);

export const validateDigimarc = createAsyncThunk(
	'scanSession/validateDigimarc',
	async (_, { dispatch }) => {
		try {
			const res = await streamApi.scan({
				filter: { method: 'digimarc', type: 'gs1:21' },
				containerId: 'stream_container',
				imageConversion: {
					greyscale: false,
					exportFormat: 'image/jpeg',
					resizeTo: 1080,
				},
			});

			if (res.length === 0) {
				dispatch(validateDigimarc());
			} else if (
				// // TODO: replace with digimarc check logic and uncomment
				// /* check digimarc against qr, return true for invalid */
				!res[0]?.results[0]?.thng
			) {
				dispatch(setIsDigimarcCorrect(/* false */ true)); // replace me once digimarc in place too!
			} else {
				dispatch(setIsDigimarcCorrect(true));
			}
		} catch (e) {
			console.error(e);

			if (e.message === 'Permission denied') {
				dispatch(actions.setHasCameraPermission(false));
			}
		} finally {
			streamApi.stop();
		}
	}
);

export const setIsDigimarcCorrect = createAsyncThunk(
	'scanSession/setIsDigimarcCorrect',
	async (isCorrect, { dispatch, getState }) => {
		dispatch(actions.setIsDigimarcCorrect(isCorrect));

		const isAuthenticated = selectIsQrCodeRecognized(await getState());
		streamApi.stop();
		const alerts = selectAuthAlerts(getState());
		if (!isAuthenticated) {
			generateAlertTrackingEvents('step2', alerts);
		}
		history.push(
			`verificationResults/${isCorrect ? 'success' : 'counterfeit'}`
		);
	}
);

export const findProduct = createAsyncThunk(
	'scanSession/findProduct',
	async (thngId, { dispatch }) => {
		const { product } = await thngApi.get(thngId);
		if (product) {
			await dispatch(getProduct(product));
		}
	}
);

export const getProduct = createAsyncThunk(
	'scanSession/getProduct',
	async (productId, { dispatch }) => {
		const { setProduct } = actions;
		const product = JSON.parse(
			JSON.stringify(await productApi.get(productId).read())
		); // evt returns instantiated objects that can't be stored in the redux store, we need to clean it for storage

		await dispatch(setProduct(product));
	}
);
